import { ReactNode, ComponentType } from 'react';
import { Loader2 } from 'lucide-react';

// --- Tiny className helper ---
function cn(...args: Array<string | false | null | undefined>) {
  return args.filter(Boolean).join(' ');
}

// --- PageHeader ----------------------------------------------------------
export function PageHeader({
  title, subtitle, eyebrow, actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-[11px] font-medium uppercase tracking-widest text-[#2d5016] mb-1">
            {eyebrow}
          </div>
        )}
        <h1 className="text-2xl font-semibold leading-tight text-gray-900 truncate">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// --- Section -------------------------------------------------------------
export function Section({
  title, subtitle, actions, children, className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('bg-white rounded-2xl border border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.02)] p-6 mb-6', className)}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold text-gray-900 leading-tight">{title}</h2>}
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// --- StatCard ------------------------------------------------------------
export function StatCard({
  icon: Icon, label, value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1 text-gray-900">
        {value === undefined || value === null ? '—' : value}
      </div>
    </div>
  );
}

// --- Button --------------------------------------------------------------
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  loading?: boolean;
  icon?: ComponentType<{ className?: string }>;
};

export function Button({
  variant = 'primary', size = 'md', loading, icon: Icon, children, className, disabled, ...rest
}: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#2d5016]';
  const sizes = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 text-sm',
  };
  const variants = {
    primary:   'bg-[#2d5016] text-white hover:bg-[#1a3010]',
    secondary: 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50',
    ghost:     'bg-transparent text-gray-700 hover:bg-gray-100',
    danger:    'bg-white text-red-700 border border-red-200 hover:bg-red-50',
  };
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(base, sizes[size], variants[variant], className)}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

// --- Badge ---------------------------------------------------------------
type BadgeTone = 'green' | 'gray' | 'amber' | 'red' | 'blue' | 'lime';

const badgeTones: Record<BadgeTone, string> = {
  green: 'bg-green-100 text-green-800 border-green-200',
  gray:  'bg-gray-100 text-gray-700 border-gray-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  red:   'bg-red-100 text-red-800 border-red-200',
  blue:  'bg-blue-100 text-blue-800 border-blue-200',
  lime:  'bg-[#c6f806]/25 text-[#2d5016] border-[#2d5016]/20',
};

export function Badge({
  tone = 'gray', children, icon: Icon, className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
      badgeTones[tone],
      className,
    )}>
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
}

// --- EmptyState ----------------------------------------------------------
export function EmptyState({
  icon: Icon, title, description, action,
}: {
  icon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="py-10 px-6 text-center">
      {Icon && (
        <div className="mx-auto w-11 h-11 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center mb-3">
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
      )}
      <div className="text-sm font-medium text-gray-900">{title}</div>
      {description && <div className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// --- Modal wrapper -------------------------------------------------------
export function Modal({
  title, subtitle, onClose, children, wide,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={cn(
        'bg-white rounded-2xl shadow-xl w-full my-8',
        wide ? 'max-w-2xl' : 'max-w-md',
      )}>
        <div className="flex items-start justify-between gap-3 p-6 border-b border-gray-100">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M4.28 4.22a.75.75 0 0 1 1.06 0L10 8.88l4.66-4.66a.75.75 0 1 1 1.06 1.06L11.06 9.94l4.66 4.66a.75.75 0 1 1-1.06 1.06L10 11l-4.66 4.66a.75.75 0 1 1-1.06-1.06l4.66-4.66-4.66-4.66a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// --- Inline status message ----------------------------------------------
export function StatusLine({ kind = 'info', children }: { kind?: 'info' | 'success' | 'error'; children: ReactNode }) {
  const classes = {
    info:    'text-gray-700 bg-gray-50 border-gray-200',
    success: 'text-green-700 bg-green-50 border-green-200',
    error:   'text-red-700 bg-red-50 border-red-200',
  }[kind];
  return <div className={cn('text-sm rounded-lg border p-2.5', classes)}>{children}</div>;
}

// --- Input + helper wrappers --------------------------------------------
export function Field({
  label, hint, children, className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block', className)}>
      <div className="text-xs font-medium text-gray-700 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </label>
  );
}

const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-[#2d5016] focus:ring-1 focus:ring-[#2d5016] focus:outline-none transition-colors';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(inputCls, props.className)} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn(inputCls, 'pr-8', props.className)} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        'w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-[#2d5016] focus:ring-1 focus:ring-[#2d5016] focus:outline-none transition-colors',
        props.className,
      )}
    />
  );
}

// --- Checkbox ------------------------------------------------------------
export function Checkbox({
  checked, onChange, label, hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#2d5016]"
      />
      <span>
        <span className="text-sm text-gray-900">{label}</span>
        {hint && <span className="block text-[11px] text-gray-500">{hint}</span>}
      </span>
    </label>
  );
}
